import {
  AdminHeader,
  AdminSingleBrandPicker,
  AdminSingleLocationPicker,
  AdminSpinner,
  BrandType,
  Div,
  SimpleBrandEntity,
  Tab,
  getBrandById,
  isAdmin,
  useUIState,
  reportError,
  useOwnBrands,
} from '@vgrubs/components';
import {
  getLocationsByIds,
  getOwnBrandsV2,
} from 'components/src/api/locations.api';
import { OwnBrandEntity } from 'components/src/types/brand.types';
import { useLocationBrandPicker } from 'components/src/store/useLocationBrandPicker';
import {
  HolidayHoursReponseV2,
  SimpleLocationEntity,
} from 'components/src/types/location.types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HolidayHours } from './sections/HolidayHours';
import { WorkingHours } from './sections/WorkingHours';

const ownBrandToSimple = (brand: OwnBrandEntity): SimpleBrandEntity => ({
  id: brand.id,
  name: brand.name,
  isLocation: false,
  isVGrubsBrand: false,
});

/** A merchant only reaches its own brands here: `/v1/brands/:id` is admin
 * only, and vGrubs brands are not theirs to edit. */
async function findOwnBrand(locationId: number, brandId: number) {
  const { data } = await getOwnBrandsV2(locationId);
  const brand = data.data.find((b) => b.id === brandId);
  return brand ? ownBrandToSimple(brand) : null;
}

export const WorkingHolidayHours = () => {
  const { workingHoursTab, setWorkingHoursTab } = useUIState((state) => state);
  const [addDayModal, setAddDayModal] = useState<
    | boolean
    | {
        edit: HolidayHoursReponseV2 | null;
      }
    | null
  >(null);

  const {
    selectedLocation,
    setSelectedLocation,
    selectedBrand,
    setSelectedBrand,
  } = useLocationBrandPicker((state) => state);

  // Merchants pick from their own brands; admins from every brand on the
  // location (AdminSingleBrandPicker reads those off the location).
  const { ownBrands } = useOwnBrands(selectedLocation?.id, {
    enabled: !isAdmin,
  });
  const showBrandPicker = isAdmin || ownBrands.length > 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingInitialUrlData, setLoadingInitialUrlData] = useState(() =>
    Boolean(searchParams.get('locationIds') || searchParams.get('brandIds'))
  );

  useEffect(() => {
    const locationIdsParam = searchParams.get('locationIds');
    const brandIdsParam = searchParams.get('brandIds');

    if (!locationIdsParam && !brandIdsParam) return;

    const parseFirstId = (value: string | null) => {
      if (!value) return null;
      const id = parseInt(value.split(',')[0]?.trim() ?? '', 10);
      return Number.isNaN(id) ? null : id;
    };

    const locationId = parseFirstId(locationIdsParam);
    const brandId = parseFirstId(brandIdsParam);

    async function loadFromUrl() {
      try {
        const loadBrand = async (): Promise<SimpleBrandEntity | null> => {
          if (!brandId) return null;
          if (!isAdmin) {
            return locationId ? findOwnBrand(locationId, brandId) : null;
          }
          const { data } = await getBrandById(brandId);
          return {
            id: data.data.id,
            name: data.data.name,
            isLocation: data.data.isLocation,
            isVGrubsBrand: data.data.type === BrandType.vGrubs,
          };
        };

        const [locationRes, brand] = await Promise.all([
          locationId ? getLocationsByIds([locationId]) : Promise.resolve(null),
          loadBrand(),
        ]);

        const location = locationRes?.data.data[0] ?? null;

        if (location) {
          setSelectedLocation(location);
        }

        setSelectedBrand(brand);
      } catch (error) {
        reportError(error);
      } finally {
        setLoadingInitialUrlData(false);
        setSearchParams(
          (prev) => {
            prev.delete('locationIds');
            prev.delete('brandIds');
            return prev;
          },
          { replace: true }
        );
      }
    }

    loadFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocationsSubmit = (l: SimpleLocationEntity) => {
    setSelectedBrand(null);
  };

  // async function getProvidersStatuses() {
  //   if (!selectedLocation) return;

  //   const locationOrBrandFunction = selectedBrand
  //     ? getBrandProviderStatuses
  //     : getLocationProviderStatuses;
  //   const locationOrBrandId = selectedBrand
  //     ? selectedBrand?.id
  //     : selectedLocation?.id;

  //   try {
  //     setLoading(true);
  //     const { data } = await locationOrBrandFunction(locationOrBrandId);

  //     const isUnavailable =
  //       firstWhereOrNull(data.data, (e) => e.provider === 'delivery') === null;

  //     setUnavailable(isUnavailable);

  //     setLoading(false);
  //   } catch (error) {
  //     ErrorToast(handleError(error));
  //     setLoading(false);
  //   }
  // }

  // useEffect(() => {
  //   getProvidersStatuses();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedBrand, selectedLocation]);

  return (
    <>
      {loadingInitialUrlData && <AdminSpinner />}
      <AdminHeader title="Working Hours" />

      <Div $flex $gap="32px" $gapSM="8px" $columnSM $m="32px 0" $mSM="16px 0">
        {!loadingInitialUrlData && (
          <>
            <Div
              $width={showBrandPicker ? '50%' : '100%'}
              $widthSM="100%"
              $flex
              $column
              $gap="20px"
            >
              <AdminSingleLocationPicker
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                handleSubmit={(l) => handleLocationsSubmit(l)}
              />
            </Div>
            {showBrandPicker && (
              <Div $width="50%" $widthSM="100%" $flex $column $gap="20px">
                <AdminSingleBrandPicker
                  selectedLocation={selectedLocation}
                  selectedBrand={selectedBrand}
                  setSelectedBrand={setSelectedBrand}
                  brands={isAdmin ? undefined : ownBrands.map(ownBrandToSimple)}
                  placeholder={isAdmin ? undefined : 'Location hours'}
                />
              </Div>
            )}
          </>
        )}
      </Div>

      <Div $flex $gap="24px" $gapSM="16px" $m="20px 0" $mSM="16px 0">
        <Tab
          $widthSM="calc(50% - 8px)"
          selected={workingHoursTab === 1}
          onClick={() => setWorkingHoursTab(1)}
        >
          Working Hours
        </Tab>
        <Tab
          $widthSM="calc(50% - 8px)"
          selected={workingHoursTab === 2}
          onClick={() => setWorkingHoursTab(2)}
        >
          Holiday Hours
        </Tab>
      </Div>

      {workingHoursTab === 1 && (
        <WorkingHours
          selectedBrand={selectedBrand}
          selectedLocation={selectedLocation}
        />
      )}
      {workingHoursTab === 2 && (
        <HolidayHours
          selectedBrand={selectedBrand}
          selectedLocation={selectedLocation}
          setAddDayModal={setAddDayModal}
          addDayModal={addDayModal}
        />
      )}

      {/* {unavailable && <Unavailable />} */}
    </>
  );
};
